// assets/AllCourses/Exercises/FillInTheBlanksExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function FillInTheBlanksExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.fillInTheBlanksQuestions)
    ? exercise.fillInTheBlanksQuestions
    : [];

  /* ================= STATE ================= */
  const [state, setState] = useState({
    questions: [],
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESET ON LOAD / RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    const freshState = {
      questions: questions.map(q => ({
        userAnswers: Array((q.sentence.match(/_+/g) || []).length).fill(""),
      })),
      hasInteracted: false,
    };

    setState(freshState);
    completedRef.current = false;
  }, [exercise._id, questions]);

  /* ================= RESET FLAG ================= */
  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE ================= */
  const updateQuestions = next => {
    if (isEditMode) onUpdate("fillInTheBlanksQuestions", next);
  };

  const updateQuestionField = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      sentence: "",
      answers: [],
      requireCapitalization: false,
    };

    let updated = { ...current, [field]: value };

    if (field === "sentence") {
      const blanks = (value.match(/_+/g) || []).length;
      updated.answers = Array.from(
        { length: blanks },
        (_, i) => current.answers?.[i] || ""
      );
    }

    next[qIdx] = updated;
    updateQuestions(next);
  };

  /* ================= HANDLE INPUT ================= */
  const handleInputChange = (qIdx, blankIdx, value) => {
    if (isEditMode || showResults) return;

    setState(prev => {
      const next = structuredClone(prev);

      next.questions[qIdx].userAnswers[blankIdx] = value;
      next.hasInteracted = true;

      setAttempt(next);

      // 🔥 VERY IMPORTANT: always notify parent
      onComplete(next, false);

      const allFilled = next.questions.every(q =>
        q.userAnswers.every(v => v.trim() !== "")
      );

      if (allFilled && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(next);
      }

      return next;
    });
  };

  /* ================= CHECK ANSWERS ================= */
  const checkAnswers = currentState => {
    const allCorrect = questions.every((q, qIdx) => {
      const blanks = (q.sentence.match(/_+/g) || []).length;

      return Array.from({ length: blanks }).every((_, aIdx) => {
        const user = normalize(
          currentState.questions[qIdx]?.userAnswers[aIdx] || "",
          q.requireCapitalization
        );

        const correct = normalize(
          q.answers[aIdx] || "",
          q.requireCapitalization
        );

        return user === correct;
      });
    });

    onComplete(currentState, allCorrect);
  };

  /* ================= EDIT UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => {
          const blanks = (q.sentence.match(/_+/g) || []).length;

          return (
            <Card key={qIdx} className="p-6">
              <div className="space-y-6">
                <TextareaField
                  label={`Sentence ${qIdx + 1}`}
                  value={q.sentence || ""}
                  onChange={v => updateQuestionField(qIdx, "sentence", v)}
                  placeholder="The ___ is shining."
                  rows={4}
                  required
                />

                {Array.from({ length: blanks }).map((_, i) => (
                  <StringField
                    key={i}
                    label={`Blank ${i + 1}`}
                    value={q.answers[i] || ""}
                    onChange={v => {
                      const next = [...questions];
                      next[qIdx].answers[i] = v;
                      updateQuestions(next);
                    }}
                  />
                ))}

                <CapitalizationRule
                  checked={!!q.requireCapitalization}
                  onChange={c =>
                    updateQuestionField(qIdx, "requireCapitalization", c)
                  }
                />

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() =>
                    updateQuestions(questions.filter((_, i) => i !== qIdx))
                  }
                >
                  Remove Question
                </Button>
              </div>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                sentence: "",
                answers: [],
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT UI ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No fill-in-the-blanks questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Fill in the Blanks</CardTitle>
        <CardDescription>
          Type correct words to complete sentences
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const parts = q.sentence.split(/(_+)/g).filter(Boolean);
          const qState = state.questions[qIdx] || { userAnswers: [] };

          let blankIndex = 0;

          return (
            <div
              key={qIdx}
              className="rounded-xl border bg-muted/40 p-6 flex flex-wrap gap-3"
            >
              {parts.map((part, i) => {
                if (/_+/.test(part)) {
                  const bIdx = blankIndex++;
                  const userValue = qState.userAnswers[bIdx] || "";
                  const correct = q.answers[bIdx] || "";

                  const isCorrect =
                    normalize(userValue, q.requireCapitalization) ===
                    normalize(correct, q.requireCapitalization);

                  return (
                    <div key={i} className="flex flex-col items-center">
                      <Input
                        value={userValue}
                        onChange={e =>
                          handleInputChange(qIdx, bIdx, e.target.value)
                        }
                        disabled={showResults}
                        className={cn(
                          "w-32 text-center font-medium border",
                          showResults &&
                            (isCorrect
                              ? "bg-green-100 border-green-400"
                              : userValue && "bg-red-100 border-red-400"),
                          showResults && "cursor-not-allowed opacity-95"
                        )}
                      />

                      {showResults && (
                        <span
                          className={cn(
                            "mt-1 text-xs font-medium",
                            isCorrect ? "text-green-600" : "text-red-600"
                          )}
                        >
                          {isCorrect ? "Correct" : `Correct: ${correct}`}
                        </span>
                      )}
                    </div>
                  );
                }

                return <span key={i}>{part}</span>;
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}